import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canCreateAsset, isAdminRole } from "@/lib/permissions";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { PhotoAutoFill } from "@/components/AIFeatures";
import AssetNameSelect, { isCuratedCategory } from "@/components/AssetNameSelect";
import { BrandInput } from "@/components/BrandInput";
import { LocationSelect } from "@/components/LocationSelect";
import { MultiMachinePicker } from "@/components/MachinePicker";

const FALLBACK_TANGIBLE = ["Machinery", "Electronics", "Furniture & Fixtures", "Machinery Spare Part", "Vehicle", "Attachments and Jigs", "Other"];
const FALLBACK_INTANGIBLE = ["Software", "Antivirus", "Firewall", "License", "Other Intangibles"];

export default function AssetForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "", asset_type: "tangible", category: "Machinery", serial_number: "", manufacturer: "", brand: "",
    model_number: "", location: "", assigned_to: "",
    purchase_date: new Date().toISOString().slice(0, 10),
    installation_date: "",
    purchase_value: "", salvage_value: "0", useful_life_years: "5",
    depreciation_method: "SLM", depreciation_rate: "15",
    status: "active", description: "", image_url: "", warranty_expires: "",
    amc_vendor: "", amc_start_date: "", amc_end_date: "", amc_cost: "0",
    maintenance_frequency_months: "",
    compatible_asset_ids: [], compatible_machine_types: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    if (form.category !== "Attachments and Jigs" || machines.length) return;
    api.get("/assets").then(({ data }) => setMachines(data.filter((a) => a.category !== "Attachments and Jigs" && a.id !== id))).catch(() => {});
  }, [form.category, machines.length, id]);

  useEffect(() => {
    if (!canCreateAsset(user?.role)) {
      toast.error("You do not have permission to create/edit assets.");
      navigate("/assets");
    }
    if (isEdit) {
      (async () => {
        try {
          const { data } = await api.get(`/assets/${id}`);
          setForm({
            name: data.name || "",
            asset_type: data.asset_type || "tangible",
            category: data.category || "Machinery",
            serial_number: data.serial_number || "", manufacturer: data.manufacturer || "",
            brand: data.brand || "",
            model_number: data.model_number || "", location: data.location || "",
            assigned_to: data.assigned_to || "",
            purchase_date: (data.purchase_date || "").slice(0, 10),
            installation_date: (data.installation_date || "").slice(0, 10),
            purchase_value: String(data.purchase_value ?? ""),
            salvage_value: String(data.salvage_value ?? "0"),
            useful_life_years: String(data.useful_life_years ?? "5"),
            depreciation_method: data.depreciation_method || "SLM",
            depreciation_rate: String(data.depreciation_rate ?? "15"),
            status: data.status || "active",
            description: data.description || "",
            image_url: data.image_url || "",
            warranty_expires: (data.warranty_expires || "").slice(0, 10),
            amc_vendor: data.amc_vendor || "",
            amc_start_date: (data.amc_start_date || "").slice(0, 10),
            amc_end_date: (data.amc_end_date || "").slice(0, 10),
            amc_cost: String(data.amc_cost ?? "0"),
            maintenance_frequency_months: data.maintenance_frequency_months ? String(data.maintenance_frequency_months) : "",
            is_returnable: data.is_returnable ?? true,
            default_deployment_asset_id: data.default_deployment_asset_id || "",
            compatible_asset_ids: data.compatible_asset_ids || [],
            compatible_machine_types: data.compatible_machine_types || [],
          });
        } catch (e) { toast.error("Failed to load asset"); }
      })();
    }
    // eslint-disable-next-line
  }, [id]);

  // Fetch category list from API (falls back to hardcoded list if API fails)
  const [tangibleCats, setTangibleCats] = useState(FALLBACK_TANGIBLE);
  const [intangibleCats, setIntangibleCats] = useState(FALLBACK_INTANGIBLE);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/asset-categories");
        const t = data.filter((c) => c.asset_type === "tangible").map((c) => c.name);
        const i = data.filter((c) => c.asset_type === "intangible").map((c) => c.name);
        if (t.length) setTangibleCats(t);
        if (i.length) setIntangibleCats(i);
      } catch { /* keep fallback */ }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When asset_type changes, reset category if incompatible
  const handleTypeChange = (v) => {
    setForm((f) => {
      const allowed = v === "tangible" ? tangibleCats : intangibleCats;
      const cat = allowed.includes(f.category) ? f.category : allowed[0];
      return { ...f, asset_type: v, category: cat };
    });
  };

  const availableCategories = form.asset_type === "tangible" ? tangibleCats : intangibleCats;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        installation_date: form.installation_date || null,
        purchase_value: parseFloat(form.purchase_value) || 0,
        salvage_value: parseFloat(form.salvage_value) || 0,
        useful_life_years: parseInt(form.useful_life_years) || 5,
        depreciation_rate: parseFloat(form.depreciation_rate) || 15,
        warranty_expires: form.warranty_expires || null,
        amc_start_date: form.amc_start_date || null,
        amc_end_date: form.amc_end_date || null,
        amc_cost: parseFloat(form.amc_cost) || 0,
        maintenance_frequency_months: form.maintenance_frequency_months
          ? parseInt(form.maintenance_frequency_months)
          : null,
        is_returnable: form.category === "Attachments and Jigs" ? Boolean(form.is_returnable) : null,
        default_deployment_asset_id: form.category === "Attachments and Jigs" ? (form.default_deployment_asset_id || null) : null,
        compatible_asset_ids: form.category === "Attachments and Jigs" ? form.compatible_asset_ids : null,
        compatible_machine_types: form.category === "Attachments and Jigs" ? form.compatible_machine_types : null,
      };
      // remove empty strings for optional fields
      ["serial_number","manufacturer","brand","model_number","location","assigned_to","description","image_url","amc_vendor"].forEach(k => {
        if (payload[k] === "") payload[k] = null;
      });
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/assets/${id}`, payload);
        saved = data;
        toast.success("Asset updated");
      } else {
        const { data } = await api.post("/assets", payload);
        saved = data;
        toast.success("Asset created");
      }
      navigate(`/assets/${saved.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="asset-form-page">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">
          {isEdit ? "Edit" : "New"} Asset
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">
          {isEdit ? "Update Asset Details" : "Register a New Asset"}
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-base">General Information</CardTitle>
            {!isEdit && (
              <PhotoAutoFill
                onAnalyzed={(d) => setForm((f) => {
                  const nextType = d.asset_type || f.asset_type;
                  const nextCat = d.category || f.category;
                  return {
                    ...f,
                    asset_type: nextType,
                    category: nextCat,
                    name: d.name || f.name,
                    manufacturer: d.manufacturer || f.manufacturer,
                    model_number: d.model_number || f.model_number,
                    description: d.description || f.description,
                  };
                })}
              />
            )}
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <div className="md:col-span-2">
              <Label>
                Name *
                {isCuratedCategory(form.category) && (
                  <span className="text-xs font-normal text-slate-500 ml-1">
                    — pick from {form.category} list
                  </span>
                )}
              </Label>
              {isEdit && !(isAdminRole(user?.role) || (user?.secondary_roles || []).some(isAdminRole)) ? (
                <>
                  <Input value={form.name} disabled data-testid="asset-name-locked" className="bg-slate-50 text-slate-500" />
                  <p className="text-[11px] text-slate-400 mt-1">Only administrators can rename an asset.</p>
                </>
              ) : (
                <AssetNameSelect
                  category={form.category}
                  value={form.name}
                  onChange={(v) => set("name", v)}
                  required
                  testid="asset-name"
                />
              )}
            </div>
            <div>
              <Label>Asset Type *</Label>
              <Select value={form.asset_type} onValueChange={handleTypeChange}>
                <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tangible">Tangible</SelectItem>
                  <SelectItem value="intangible">Intangible</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {form.asset_type === "intangible"
                  ? "Intangible: Software, Antivirus, Firewall, License, Other Intangibles"
                  : "Physical assets like machinery, electronics, furniture, vehicles"}
              </p>
            </div>
            <div>
              <Label>Category *</Label>
              <Select key={form.asset_type} value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="asset-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="under_maintenance">Under Maintenance</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                  <SelectItem value="disposed">Disposed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Serial Number</Label><Input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} /></div>
            <div><Label>Invoicing Party</Label><Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} data-testid="asset-invoicing-party" /></div>
            <div><Label>Brand</Label><BrandInput value={form.brand} onChange={(v) => set("brand", v)} testid="asset-brand" /></div>
            <div><Label>Model Number</Label><Input value={form.model_number} onChange={(e) => set("model_number", e.target.value)} /></div>
            <div><Label>Location</Label><LocationSelect value={form.location} onChange={(v) => set("location", v)} testId="asset-location-select" /></div>
            <div><Label>Assigned To</Label><Input value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} /></div>
            <div><Label>Warranty Expires</Label><Input type="date" value={form.warranty_expires} onChange={(e) => set("warranty_expires", e.target.value)} /></div>
            <div className="md:col-span-2">
              <Label>Image URL</Label>
              <Input placeholder="https://..." value={form.image_url} onChange={(e) => set("image_url", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Financial & Depreciation</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <div><Label>Purchase Date *</Label><Input required type="date" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} data-testid="asset-purchase-date" /><p className="text-[11px] text-slate-500 mt-1">Depreciation is calculated from this date.</p></div>
            <div><Label>Installation Date</Label><Input type="date" value={form.installation_date} onChange={(e) => set("installation_date", e.target.value)} data-testid="asset-installation-date" /><p className="text-[11px] text-slate-500 mt-1">AMC & maintenance schedule start from this date. Leave blank to use today.</p></div>
            <div><Label>Purchase Value (₹) *</Label><Input required type="number" value={form.purchase_value} onChange={(e) => set("purchase_value", e.target.value)} data-testid="asset-purchase-value" /></div>
            <div><Label>Salvage Value (₹)</Label><Input type="number" value={form.salvage_value} onChange={(e) => set("salvage_value", e.target.value)} /></div>
            <div><Label>Useful Life (years)</Label><Input type="number" value={form.useful_life_years} onChange={(e) => set("useful_life_years", e.target.value)} /></div>
            <div>
              <Label>Depreciation Method</Label>
              <Select value={form.depreciation_method} onValueChange={(v) => set("depreciation_method", v)}>
                <SelectTrigger data-testid="asset-depreciation-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SLM">Straight-Line (SLM)</SelectItem>
                  <SelectItem value="WDV">Written-Down Value (WDV)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Depreciation Rate (%) — for WDV</Label><Input type="number" step="0.1" value={form.depreciation_rate} onChange={(e) => set("depreciation_rate", e.target.value)} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-base">AMC (Annual Maintenance Contract)</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <div className="md:col-span-2"><Label>AMC Vendor</Label><Input value={form.amc_vendor} onChange={(e) => set("amc_vendor", e.target.value)} placeholder="e.g. Haas India Services" data-testid="amc-vendor" /></div>
            <div><Label>AMC Start Date</Label><Input type="date" value={form.amc_start_date} onChange={(e) => set("amc_start_date", e.target.value)} data-testid="amc-start-date" /></div>
            <div><Label>AMC End Date</Label><Input type="date" value={form.amc_end_date} onChange={(e) => set("amc_end_date", e.target.value)} data-testid="amc-end-date" /></div>
            <div className="md:col-span-2"><Label>AMC Annual Cost (₹)</Label><Input type="number" value={form.amc_cost} onChange={(e) => set("amc_cost", e.target.value)} data-testid="amc-cost" /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Auto Maintenance Schedule</CardTitle></CardHeader>
          <CardContent className="p-6 space-y-3">
            <p className="text-xs text-slate-600">
              The system will auto-create 4 upcoming preventive maintenance entries based on the frequency below.
              Leave blank to use the category default: Machinery/Vehicle = 3 months, Electronics = 6 months, all others = 12 months.
            </p>
            <div>
              <Label>Maintenance Frequency</Label>
              <Select
                value={form.maintenance_frequency_months || "default"}
                onValueChange={(v) => set("maintenance_frequency_months", v === "default" ? "" : v)}
              >
                <SelectTrigger data-testid="maintenance-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Use category default</SelectItem>
                  <SelectItem value="1">Monthly</SelectItem>
                  <SelectItem value="3">Every 3 months (Quarterly)</SelectItem>
                  <SelectItem value="6">Every 6 months (Half-yearly)</SelectItem>
                  <SelectItem value="12">Every 12 months (Annual)</SelectItem>
                  <SelectItem value="24">Every 24 months (Biennial)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {form.category === "Attachments and Jigs" && (
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Attachment / Jig Settings</CardTitle></CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label>Deployment Type *</Label>
                <Select value={form.is_returnable ? "returnable" : "permanent"} onValueChange={(v) => set("is_returnable", v === "returnable")}>
                  <SelectTrigger data-testid="spare-returnable-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="returnable">Returnable — spare comes back to inventory after use</SelectItem>
                    <SelectItem value="permanent">Permanent Deployment — installed on a machine and stays there</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Determines whether this spare/jig can be returned to stores after being issued to a machine.
                </p>
              </div>
              <div>
                <Label>Fits Machines / Machine Types</Label>
                <MultiMachinePicker
                  assets={machines}
                  ids={form.compatible_asset_ids}
                  types={form.compatible_machine_types}
                  onChange={({ ids, types }) => setForm((f) => ({ ...f, compatible_asset_ids: ids, compatible_machine_types: types }))}
                  testid="spare-compatible-machines"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  These machines are listed first when issuing this spare. You can still issue it to any other machine.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-asset-btn">
            {submitting ? "Saving..." : isEdit ? "Update Asset" : "Create Asset"}
          </Button>
        </div>
      </form>
    </div>
  );
}

